import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type {
    ClienteDTO,
    CreateClientRequest,
    EstadoCivil,
    Genero,
    SepomexDTO,
    Status,
    TipoDireccion,
    TipoPersona,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { toDate, toIsoDate } from '../../../utils/dates'
import { clip, onlyLetters, onlySimple } from '../../../utils/inputs'
import { calcularRFC } from '../../../utils/RFCGenerator'

export interface CustomerFormState {
    id: number | null
    personalidad: TipoPersona
    status: Status | ''
    nombres: string
    alias: string
    apPat: string
    apMat: string
    sexo: Genero | ''
    fecNacimiento: Date | null
    rfc: string
    cp: string
    edoCivil: EstadoCivil | ''
    direccionId: number | null
    direccion: string
    colonia: number | ''
    tipoDireccion: TipoDireccion | null
    celular: string
    celularId: number | null
    correo: string
    correoId: number | null
}

export interface CustomerFormProps {
    modal?: boolean
    cliente?: Partial<CustomerFormState>
    onCompleted?: (cliente?: ClienteDTO | null) => void
}

export type CustomerPanel = 'adjuntos' | 'bitacora'

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

const LOAD_ERROR = 'No se pudo cargar el cliente.'
const SAVE_ERROR = 'Error al guardar el cliente'
const DELETE_ERROR = 'No se pudo eliminar al cliente.'

const emptyState: CustomerFormState = {
    id: null,
    personalidad: 'fisica',
    status: '',
    nombres: '',
    alias: '',
    apPat: '',
    apMat: '',
    sexo: '',
    fecNacimiento: null,
    rfc: '',
    cp: '',
    edoCivil: '',
    direccionId: null,
    direccion: '',
    colonia: '',
    tipoDireccion: null,
    celular: '',
    celularId: null,
    correo: '',
    correoId: null,
}

const sameId = (left: unknown, right: unknown) => String(left ?? '') === String(right ?? '')
const onlyDigits = (value: string, max: number) => clip(value, max).replace(/\D/g, '')

const isoDate = (date: Date | null) => toIsoDate(date)

const parseApiDate = (value: string | null | undefined) => toDate(value)

const toFormState = (body: ClienteDTO): CustomerFormState => {
    const direccion = body.direcciones?.[0]
    const celular = body.celulares?.[0]
    const correo = body.correos?.[0]
    return {
        ...emptyState,
        id: body.id,
        status: body.status || '',
        personalidad: body.fiscal || 'fisica',
        nombres: body.nombres || '',
        alias: body.alias || '',
        apPat: body.apPat || '',
        apMat: body.apMat || '',
        sexo: body.sexo || '',
        fecNacimiento: parseApiDate(body.fecNacimiento),
        rfc: body.rfc || '',
        cp: direccion?.cp || '',
        edoCivil: body.edoCivil || '',
        direccionId: direccion?.id ?? null,
        direccion: direccion?.direccion || '',
        colonia: direccion?.sepomex?.id || '',
        tipoDireccion: direccion?.tipo ?? null,
        correo: correo?.correo || '',
        correoId: correo?.id ?? null,
        celular: celular?.numero || '',
        celularId: celular?.id ?? null,
    }
}

const toRequest = (state: CustomerFormState, colonia: SepomexDTO | undefined): CreateClientRequest => {
    const contacto = {
        direccion: {
            id: state.direccionId,
            tipo: state.tipoDireccion || 'fisica',
            direccion: state.direccion,
            sepomexId: colonia?.id,
        },
        celular: {
            id: state.celularId,
            numero: state.celular,
        },
        correo: {
            id: state.correoId,
            correo: state.correo,
            tipo: 'personal' as const,
        },
    }
    if (state.personalidad === 'fisica') {
        return {
            id: state.id,
            nombres: state.nombres,
            alias: state.alias,
            apPat: state.apPat,
            apMat: state.apMat,
            fecNacimiento: isoDate(state.fecNacimiento),
            fiscal: state.personalidad,
            edoCivil: state.edoCivil || null,
            sexo: state.sexo || null,
            rfc: state.rfc,
            ...contacto,
        }
    }
    return {
        id: state.id,
        nombres: state.nombres,
        alias: state.alias,
        fiscal: state.personalidad,
        rfc: state.rfc,
        ...contacto,
    }
}

const errorMessage = (error: unknown, fallback: string) => (error instanceof Error && error.message) || fallback

export const useCustomerFormModel = ({ modal, cliente, onCompleted }: CustomerFormProps) => {
    const history = useHistory()
    const location = useLocation()
    const { clienteId } = useParams()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || '/clientes'

    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [codigos, setCodigos] = useState<SepomexDTO[]>([])
    const [loadingColonias, setLoadingColonias] = useState(false)
    const [panel, setPanel] = useState<CustomerPanel | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [state, setState] = useState<CustomerFormState>(emptyState)

    useEffect(() => {
        const parametroClienteId = clienteId || cliente?.id
        if (!parametroClienteId) {
            if (cliente) setState((current) => ({ ...current, ...cliente }))
            return undefined
        }
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<ClienteDTO> | null = await (await APIInvoke.invokeGET(`/clientes/${parametroClienteId}`)).json()
                if (!active) return
                if (!response?.ok || !response.body) throw new Error(response?.message || LOAD_ERROR)
                setState(toFormState(response.body))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [clienteId, cliente])

    useEffect(() => {
        if (state.cp.length !== 5) {
            setCodigos([])
            setLoadingColonias(false)
            return undefined
        }
        let active = true
        const load = async () => {
            setLoadingColonias(true)
            try {
                const response: WrapperResponse<SepomexDTO[]> | null = await (await APIInvoke.invokeGET(`/sepomex/${state.cp}`)).json()
                if (!active) return
                const items = response?.body || []
                setCodigos(items)
                setState((current) => {
                    if (current.cp !== state.cp) return current
                    const colonia = items.find((item) => sameId(item.id, current.colonia))
                    if (colonia?.id != null) return { ...current, colonia: colonia.id }
                    if (items.length === 1 && items[0].id != null) return { ...current, colonia: items[0].id }
                    return { ...current, colonia: '' }
                })
            } catch (error) {
                console.error(error)
                if (active) setCodigos([])
            } finally {
                if (active) setLoadingColonias(false)
            }
        }
        load()
        return () => { active = false }
    }, [state.cp])

    useEffect(() => {
        try {
            const date = state.fecNacimiento
            if (state.personalidad !== 'fisica' || !date || state.nombres.length === 0 || state.apMat.length === 0) return
            const rfc: string | undefined = calcularRFC(
                state.apPat,
                state.apMat,
                state.nombres,
                `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
            )
            if (!rfc) return
            setState((current) => (current.rfc === rfc ? current : { ...current, rfc }))
        } catch (error) {
            console.log('invalida RFC', error)
        }
    }, [state.nombres, state.apPat, state.apMat, state.fecNacimiento, state.personalidad])

    const patch = (partial: Partial<CustomerFormState>) => setState((current) => ({ ...current, ...partial }))
    const esFisica = state.personalidad === 'fisica'
    const selectedColonia = codigos.find((item) => sameId(item.id, state.colonia))

    const change = {
        personalidad: (value: TipoPersona) => patch({ personalidad: value }),
        nombres: (value: string) => patch({ nombres: esFisica ? onlyLetters(value, 200) : onlySimple(value, 200) }),
        apPat: (value: string) => patch({ apPat: onlyLetters(value, 60) }),
        apMat: (value: string) => patch({ apMat: onlyLetters(value, 60) }),
        alias: (value: string) => patch({ alias: onlyLetters(value, 50) }),
        fecNacimiento: (value: string) => patch({ fecNacimiento: parseApiDate(value) }),
        sexo: (value: Genero | '') => patch({ sexo: value }),
        rfc: (value: string) => patch({ rfc: onlySimple(value, esFisica ? 13 : 12).toUpperCase() }),
        edoCivil: (value: EstadoCivil | '') => patch({ edoCivil: value }),
        direccion: (value: string) => patch({ direccion: clip(value, 150) }),
        cp: (value: string) => patch({ cp: onlyDigits(value, 5) }),
        colonia: (value: number | string) => patch({ colonia: value === '' ? '' : Number(value) }),
        correo: (value: string) => patch({ correo: clip(value, 100) }),
        celular: (value: string) => patch({ celular: value }),
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            setLoading(true)
            setMessage(null)
            const request = toRequest(state, selectedColonia)
            const response: WrapperResponse<ClienteDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/clientes/${clienteId || state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/clientes', request)).json()

            if (response.ok) {
                setMessage({ type: 'success', message: 'Cliente guardado correctamente' })
                if (onCompleted) onCompleted(response.body)
                else history.push({ pathname: '/clientes', state: { backUrl: '/clientes' } })
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar el cliente', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setLoading(false)
        }
    }

    const deleteCliente = async () => {
        if (!state.id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            setMessage(null)
            const deleteResponse: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/clientes/${state.id}`)).json()
            if (deleteResponse.ok) {
                if (onCompleted) onCompleted(deleteResponse.body)
                else history.push(backUrl)
            } else {
                setMessage({ type: 'error', message: deleteResponse.message || DELETE_ERROR })
            }
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
        }
    }

    const cancel = () => {
        if (modal) onCompleted?.()
        else history.push(backUrl)
    }

    const entityId = state.id || (clienteId ? Number(clienteId) : null)

    return {
        form: state,
        esFisica,
        fecNacimientoValue: isoDate(state.fecNacimiento) || '',
        municipio: selectedColonia?.municipio || '',
        codigos,
        loading,
        loadingColonias,
        deleting,
        busy: loading || deleting,
        busyLabel: deleting ? 'Eliminando cliente…' : clienteId && !state.id ? 'Cargando cliente…' : 'Guardando cliente…',
        message,
        title: state.id ? 'Editar cliente' : 'Nuevo cliente',
        isEdit: Boolean(state.id),
        activo: String(state.status || '').toLowerCase() === 'activo',
        backTo: modal ? undefined : backUrl,
        entityId,
        panel,
        confirmDelete,
        change,
        save,
        cancel,
        deleteCliente,
        openPanel: (value: CustomerPanel) => setPanel(value),
        closePanel: () => setPanel(null),
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
    }
}
